/**
 * fetchNearby.ts — T-451, the station gather behind the PDF one-pager's nearby block.
 *
 * WHERE THE DATA COMES FROM, after Ricardo's amendment. The POIs are no longer fetched here: the
 * /score/site payload now carries a complete all-category nearby-POI set (its supplemental gather),
 * so the map, the side panel and this export all inherit one complete payload. This helper takes
 * those POIs as an argument and fetches ONLY the charging stations — because score.ts's internal
 * station shape carries no name, connector types or per-level ports, and the nearby block needs all
 * three. One /api/ev-stations/nearby call per export; no /api/poi/search call at all.
 *
 * UNITS: /api/ev-stations/nearby takes radius in MILES (the metres-vs-miles trap that made the old
 * two-endpoint version fragile is gone with the second endpoint).
 */
import { authedFetch } from '../api'
import type { NearbyPoiPayload } from '../tractModel'
import {
  summarizeNearby,
  DEFAULT_NEARBY_RADII_MI,
  type NearbyStats,
  type NearbyStation,
  type NearbyPoi,
} from '../nearbyStats'

/**
 * ALS SearchNearby's hard result ceiling, mirrored from apps/api routes/score.ts
 * NEARBY_POI_MAX_RESULTS. The site payload's `poiFetched` is compared to this to decide whether the
 * amenity counts are floors; the value is also what the footnote states as the bound.
 */
export const NEARBY_POI_RESULT_CAP = 100

/** The `limit` the station lookup asks for; also what decides whether its counts are a floor. */
export const NEARBY_STATION_LIMIT = 200

/**
 * Stations are gathered WIDER than the widest presented ring on purpose. The rings are
 * presentation; the search is not. Without this, a site with its nearest competitor at 3 miles
 * prints a blank where the nearest-station line should be, and a blank reads as "no competition"
 * — a much more flattering claim than "none within a mile".
 */
export const NEARBY_STATION_GATHER_MI = 5

interface StationResponse {
  count?: number
  stations?: NearbyStation[]
}

/**
 * Fetch the charging stations and summarize them against the POIs already in the site payload.
 *
 * DEGRADATION, by half rather than all-or-nothing. The POIs arrived with the score, so a failed
 * station fetch does not cost the amenity side: the block still renders with an empty station set
 * (its lines say there are none, which is honest — nothing was found because nothing was fetched).
 * The block is omitted entirely (null) only when there is nothing to show at all: no POIs AND no
 * stations. The document has a one-line explanation for that case.
 */
export async function fetchNearbyStats(
  lat: number,
  lng: number,
  nearbyPois: readonly NearbyPoiPayload[],
  poiFetched: number,
  radiiMi: readonly number[] = DEFAULT_NEARBY_RADII_MI,
): Promise<NearbyStats | null> {
  const widestMi = radiiMi[radiiMi.length - 1]
  if (widestMi === undefined) return null

  let stations: NearbyStation[] = []
  let stationsOk = false
  try {
    const params = new URLSearchParams({
      lat: String(lat),
      lng: String(lng),
      radius: String(NEARBY_STATION_GATHER_MI),
      limit: String(NEARBY_STATION_LIMIT),
    })
    const res = await authedFetch(`/api/ev-stations/nearby?${params}`)
    if (res.ok) {
      const body = (await res.json()) as StationResponse
      stations = body.stations ?? []
      stationsOk = true
    }
  } catch {
    // Offline / aborted / unparseable — leave stations empty, stationsOk false.
  }

  // Nothing to show at all: omit the block rather than print an empty one.
  if (!stationsOk && nearbyPois.length === 0) return null

  const pois: NearbyPoi[] = nearbyPois.map((p) => ({
    id: p.id,
    category: p.category,
    name: p.name,
    lat: p.lat,
    lng: p.lng,
  }))

  return summarizeNearby(lat, lng, stations, pois, {
    gatherRadiusMi: widestMi,
    stationGatherRadiusMi: NEARBY_STATION_GATHER_MI,
    // The RAW item count from the payload, not the POI list length — the list has already been
    // through the 12-category filter, so comparing it to the cap is a check that can never fire.
    poiFetched,
    poiResultCap: NEARBY_POI_RESULT_CAP,
    // Only claim a station floor when the station fetch actually succeeded at its limit; a failed
    // fetch is not a floor, it is an absence.
    ...(stationsOk ? { stationResultCap: NEARBY_STATION_LIMIT } : {}),
    radiiMi,
  })
}
